/**
 * Utility class for creating and manipulating `IColor4` objects.
 */
export class IColor4 {
    constructor(r, g, b, a = 1) {
        if (r !== undefined && g !== undefined && b !== undefined) {
            return { r: r, g: g, b: b, a: a };
        }
        return { r: 0, g: 0, b: 0, a: 1 };
    }
    /**
     * Type guard to check if the provided object is applicable to the `IColor4` interface.
     * @returns Type assertion if the object is applicable to the IColor4.
     */
    static is(unknown) {
        const color = unknown;
        return typeof color.r === 'number' && typeof color.g === 'number' && typeof color.b === 'number' && typeof color.a === 'number';
    }
    /**
     * Checks if two `IColor4` objects are equal by comparing their components.
     * @returns True if the components of color1 and color2 are exactly equal.
     */
    static equals(color1, color2) {
        return color1.r === color2.r && color1.g === color2.g && color1.b === color2.b && color1.a === color2.a;
    }
    /**
     * Creates a new `IColor4` object as a verbose copy of the provided color.
     * @returns A new `IColor4` object.
     */
    static copy(color) {
        return new IColor4(color.r, color.g, color.b, color.a);
    }
    /**
     * Copies the values from one `IColor4` object to another.
     * @note This method directly modifies the `result` color.
     */
    static copy_to_ref(color, result) {
        result.r = color.r;
        result.g = color.g;
        result.b = color.b;
        result.a = color.a;
        return result;
    }
    /**
     * Directly sets the values to a `IColor4` object.
     * @note This method directly modifies the `target` color.
     */
    static set(color, r, g, b, a) {
        color.r = r;
        color.g = g;
        color.b = b;
        color.a = a;
        return color;
    }
    /**
     * Verifies if the provided `IColor4` is within the specified range.
     * @returns `true` if the color is within the range.
     */
    static is_in_range(color, min, max) {
        return !(color.r < min || color.r > max || color.g < min || color.g > max || color.b < min || color.b > max || color.a < min || color.a > max);
    }
    /**
     * Verifies if the provided `IColor4` is within `0 - 1` range.
     * @returns `true` if the color is within the range.
     */
    static is_saturated(color) {
        return !(color.r < 0 || color.r > 1 || color.g < 0 || color.g > 1 || color.b < 0 || color.b > 1 || color.a < 0 || color.a > 1);
    }
    /**
     * Converts provided `IVector4` to sRGB color space.
     * @returns A new `IColor4` object.
     */
    static to_sRGB(vector) {
        return {
            r: Math.pow(vector.x, 1 / 2.2),
            g: Math.pow(vector.y, 1 / 2.2),
            b: Math.pow(vector.z, 1 / 2.2),
            a: vector.w
        };
    }
    /**
     * Converts provided `IColor` to Linear color space represented as `IVector4`.
     * @returns A new `IVector4` object.
     */
    static to_linear_space(color) {
        return {
            x: Math.pow(color.r, 2.2),
            y: Math.pow(color.g, 2.2),
            z: Math.pow(color.b, 2.2),
            w: color.a
        };
    }
    /**
     * Converts an `IColor4` object to an array of numbers `[ r, g, b, a ]`.
     * @returns An array `[ number, number, number, number ]`.
     */
    static to_array(color) {
        return [color.r, color.g, color.b, color.a];
    }
    /**
     * Converts an `IColor4` object to a `string`.
     * @returns A string in the format `{ r: number, g: number, b: number, a: number }`.
     */
    static to_string(color) {
        return `{ r: ${color.r}, g: ${color.g}, b: ${color.b}, a: ${color.a} }`;
    }
}
//# sourceMappingURL=IColor4.js.map